import React, { useState, useEffect } from 'react';
import AdminLayout from '../components/layout/AdminLayout';
import { View, Text, ScrollView, TouchableOpacity, StyleSheet, Platform, RefreshControl, Dimensions } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { getUserGrowth, getEngagementMetrics, getGeographicDistribution } from '../api/analytics';
import { getColor, getSpacing, getBorderRadius, getShadow } from '../theme/adminTheme';

const AnalyticsDashboardScreen = () => {
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [period, setPeriod] = useState('30d');
    const [userGrowthData, setUserGrowthData] = useState(null);
    const [engagementData, setEngagementData] = useState(null);
    const [geoData, setGeoData] = useState(null);

    useEffect(() => {
        fetchAnalyticsData();
    }, [period]);

    const fetchAnalyticsData = async () => {
        try {
            setLoading(true);

            const [userGrowth, engagement, geographic] = await Promise.all([
                getUserGrowth(period),
                getEngagementMetrics(),
                getGeographicDistribution()
            ]);

            setUserGrowthData(userGrowth.data);
            setEngagementData(engagement.data);
            setGeoData(geographic.data);
        } catch (error) {
            console.error('Error fetching analytics:', error);
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    const onRefresh = () => {
        setRefreshing(true);
        fetchAnalyticsData();
    };

    const periods = [
        { value: '7d', label: '7 Days' },
        { value: '30d', label: '30 Days' },
        { value: '90d', label: '90 Days' },
        { value: '1y', label: '1 Year' }
    ];

    return (
        <AdminLayout>
            <ScrollView
                style={styles.container}
                contentContainerStyle={styles.contentContainer}
                refreshControl={
                    <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
                }
            >
                {/* Header */}
                <View style={styles.header}>
                    <View>
                        <Text style={styles.headerTitle}>Analytics Dashboard</Text>
                        <Text style={styles.headerSubtitle}>
                            Comprehensive insights and data visualization
                        </Text>
                    </View>

                    {/* Period Selector */}
                    <View style={styles.periodSelector}>
                        {periods.map((p) => (
                            <TouchableOpacity
                                key={p.value}
                                style={[
                                    styles.periodButton,
                                    period === p.value && styles.periodButtonActive
                                ]}
                                onPress={() => setPeriod(p.value)}
                            >
                                <Text style={[
                                    styles.periodButtonText,
                                    period === p.value && styles.periodButtonTextActive
                                ]}>
                                    {p.label}
                                </Text>
                            </TouchableOpacity>
                        ))}
                    </View>
                </View>

                {/* Engagement Metrics */}
                {engagementData && (
                    <View style={[styles.metricsCard, getShadow('md')]}>
                        <Text style={styles.cardTitle}>Engagement Overview</Text>
                        <View style={styles.metricsGrid}>
                            <MetricBox
                                label="Daily Active Users"
                                value={engagementData.dailyActiveUsers}
                                icon="people"
                                color="primary"
                            />
                            <MetricBox
                                label="Monthly Active Users"
                                value={engagementData.monthlyActiveUsers}
                                icon="people-circle"
                                color="success"
                            />
                            <MetricBox
                                label="Registration Rate"
                                value={`${engagementData.registrationRate}%`}
                                icon="trending-up"
                                color="warning"
                            />
                            <MetricBox
                                label="Total Participants"
                                value={engagementData.totalParticipants}
                                icon="person"
                                color="primary"
                            />
                        </View>
                    </View>
                )}

                {/* User Growth Chart */}
                {userGrowthData && (
                    <View style={[styles.chartCard, getShadow('md')]}>
                        <View style={styles.chartHeader}>
                            <Text style={styles.cardTitle}>User Growth Trend</Text>
                            <Text style={styles.cardSubtitle}>
                                New user registrations over time
                            </Text>
                        </View>
                        <SimpleBarChart data={userGrowthData.datasets.newUsers} labels={userGrowthData.labels} />
                    </View>
                )}

                {/* Geographic Distribution */}
                {geoData && (
                    <View style={styles.twoColumnRow}>
                        {/* By Cluster */}
                        <View style={[styles.geoCard, getShadow('md')]}>
                            <Text style={styles.cardTitle}>Distribution by Cluster</Text>
                            <View style={styles.geoList}>
                                {geoData.byCluster.map((item, index) => (
                                    <View key={index} style={styles.geoItem}>
                                        <View style={styles.geoItemHeader}>
                                            <Text style={styles.geoItemName}>{item.name}</Text>
                                            <Text style={styles.geoItemCount}>{item.count}</Text>
                                        </View>
                                        <View style={styles.geoBar}>
                                            <View
                                                style={[
                                                    styles.geoBarFill,
                                                    {
                                                        width: `${(item.count / Math.max(...geoData.byCluster.map(c => c.count))) * 100}%`,
                                                        backgroundColor: getColor('primary.500')
                                                    }
                                                ]}
                                            />
                                        </View>
                                    </View>
                                ))}
                            </View>
                        </View>

                        {/* By FCP */}
                        <View style={[styles.geoCard, getShadow('md')]}>
                            <Text style={styles.cardTitle}>Distribution by FCP</Text>
                            <View style={styles.geoList}>
                                {geoData.byFCP.slice(0, 10).map((item, index) => (
                                    <View key={index} style={styles.geoItem}>
                                        <View style={styles.geoItemHeader}>
                                            <Text style={styles.geoItemName}>{item.name}</Text>
                                            <Text style={styles.geoItemCount}>{item.count}</Text>
                                        </View>
                                        <View style={styles.geoBar}>
                                            <View
                                                style={[
                                                    styles.geoBarFill,
                                                    {
                                                        width: `${(item.count / Math.max(...geoData.byFCP.map(f => f.count))) * 100}%`,
                                                        backgroundColor: getColor('success.500')
                                                    }
                                                ]}
                                            />
                                        </View>
                                    </View>
                                ))}
                            </View>
                        </View>
                    </View>
                )}
            </ScrollView>
        </AdminLayout>
    );
};

// Simple Metric Box Component
const MetricBox = ({ label, value, icon, color }) => (
    <View style={styles.metricBox}>
        <View style={[styles.metricIcon, { backgroundColor: getColor(`${color}.50`) }]}>
            <Ionicons name={icon} size={24} color={getColor(`${color}.600`)} />
        </View>
        <Text style={styles.metricValue}>{value}</Text>
        <Text style={styles.metricLabel}>{label}</Text>
    </View>
);

// Simple Bar Chart Component
const SimpleBarChart = ({ data, labels }) => {
    const maxValue = Math.max(...data);
    const screenWidth = Dimensions.get('window').width;
    const chartWidth = Platform.OS === 'web' ? Math.min(screenWidth - 100, 800) : screenWidth - 64;

    return (
        <View style={styles.chartContainer}>
            <View style={styles.barsContainer}>
                {data.map((value, index) => (
                    <View key={index} style={styles.barWrapper}>
                        <View style={styles.barColumn}>
                            <Text style={styles.barValue}>{value}</Text>
                            <View style={styles.bar}>
                                <View
                                    style={[
                                        styles.barFill,
                                        {
                                            height: `${(value / maxValue) * 100}%`,
                                            backgroundColor: getColor('primary.500')
                                        }
                                    ]}
                                />
                            </View>
                        </View>
                        {labels[index] && (
                            <Text style={styles.barLabel} numberOfLines={1}>
                                {new Date(labels[index]).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                            </Text>
                        )}
                    </View>
                ))}
            </View>
        </View>
    );
};

const styles = StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: getColor('gray.50'),
    },
    contentContainer: {
        padding: getSpacing('lg'),
    },
    header: {
        flexDirection: Platform.OS === 'web' ? 'row' : 'column',
        justifyContent: 'space-between',
        alignItems: Platform.OS === 'web' ? 'center' : 'flex-start',
        marginBottom: getSpacing('xl'),
        gap: getSpacing('md'),
    },
    headerTitle: {
        fontSize: 28,
        fontWeight: '700',
        color: getColor('gray.900'),
        marginBottom: 4,
        ...(Platform.OS === 'web' ? { fontFamily: 'Inter, sans-serif' } : {}),
    },
    headerSubtitle: {
        fontSize: 14,
        color: getColor('gray.600'),
        ...(Platform.OS === 'web' ? { fontFamily: 'Inter, sans-serif' } : {}),
    },
    periodSelector: {
        flexDirection: 'row',
        gap: getSpacing('sm'),
        backgroundColor: getColor('gray.100'),
        padding: 4,
        borderRadius: getBorderRadius('md'),
    },
    periodButton: {
        paddingHorizontal: getSpacing('md'),
        paddingVertical: getSpacing('sm'),
        borderRadius: getBorderRadius('sm'),
        ...(Platform.OS === 'web' ? {
            cursor: 'pointer',
            transition: 'all 0.2s ease',
        } : {}),
    },
    periodButtonActive: {
        backgroundColor: '#FFFFFF',
        ...getShadow('sm'),
    },
    periodButtonText: {
        fontSize: 14,
        fontWeight: '500',
        color: getColor('gray.600'),
        ...(Platform.OS === 'web' ? { fontFamily: 'Inter, sans-serif' } : {}),
    },
    periodButtonTextActive: {
        color: getColor('primary.700'),
        fontWeight: '600',
    },
    metricsCard: {
        backgroundColor: '#FFFFFF',
        borderRadius: getBorderRadius('lg'),
        padding: getSpacing('lg'),
        marginBottom: getSpacing('lg'),
    },
    cardTitle: {
        fontSize: 18,
        fontWeight: '600',
        color: getColor('gray.900'),
        marginBottom: getSpacing('md'),
        ...(Platform.OS === 'web' ? { fontFamily: 'Inter, sans-serif' } : {}),
    },
    cardSubtitle: {
        fontSize: 14,
        color: getColor('gray.500'),
        ...(Platform.OS === 'web' ? { fontFamily: 'Inter, sans-serif' } : {}),
    },
    metricsGrid: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: getSpacing('md'),
    },
    metricBox: {
        flex: 1,
        minWidth: Platform.OS === 'web' ? 200 : '45%',
        backgroundColor: getColor('gray.50'),
        padding: getSpacing('lg'),
        borderRadius: getBorderRadius('md'),
        alignItems: 'center',
    },
    metricIcon: {
        width: 56,
        height: 56,
        borderRadius: getBorderRadius('lg'),
        justifyContent: 'center',
        alignItems: 'center',
        marginBottom: getSpacing('md'),
    },
    metricValue: {
        fontSize: 32,
        fontWeight: '700',
        color: getColor('gray.900'),
        marginBottom: 4,
        ...(Platform.OS === 'web' ? { fontFamily: 'Inter, sans-serif' } : {}),
    },
    metricLabel: {
        fontSize: 14,
        color: getColor('gray.600'),
        textAlign: 'center',
        ...(Platform.OS === 'web' ? { fontFamily: 'Inter, sans-serif' } : {}),
    },
    chartCard: {
        backgroundColor: '#FFFFFF',
        borderRadius: getBorderRadius('lg'),
        padding: getSpacing('lg'),
        marginBottom: getSpacing('lg'),
    },
    chartHeader: {
        marginBottom: getSpacing('lg'),
    },
    chartContainer: {
        paddingVertical: getSpacing('md'),
    },
    barsContainer: {
        flexDirection: 'row',
        alignItems: 'flex-end',
        justifyContent: 'space-between',
        height: 200,
        gap: 4,
    },
    barWrapper: {
        flex: 1,
        alignItems: 'center',
        gap: getSpacing('sm'),
    },
    barColumn: {
        flex: 1,
        width: '100%',
        alignItems: 'center',
        justifyContent: 'flex-end',
    },
    barValue: {
        fontSize: 10,
        fontWeight: '600',
        color: getColor('gray.700'),
        marginBottom: 4,
        ...(Platform.OS === 'web' ? { fontFamily: 'Inter, sans-serif' } : {}),
    },
    bar: {
        width: '100%',
        flex: 1,
        backgroundColor: getColor('gray.100'),
        borderRadius: getBorderRadius('sm'),
        overflow: 'hidden',
        justifyContent: 'flex-end',
    },
    barFill: {
        width: '100%',
        borderRadius: getBorderRadius('sm'),
    },
    barLabel: {
        fontSize: 10,
        color: getColor('gray.600'),
        textAlign: 'center',
        ...(Platform.OS === 'web' ? { fontFamily: 'Inter, sans-serif' } : {}),
    },
    twoColumnRow: {
        flexDirection: Platform.OS === 'web' ? 'row' : 'column',
        gap: getSpacing('lg'),
    },
    geoCard: {
        flex: 1,
        backgroundColor: '#FFFFFF',
        borderRadius: getBorderRadius('lg'),
        padding: getSpacing('lg'),
    },
    geoList: {
        gap: getSpacing('md'),
    },
    geoItem: {
        gap: getSpacing('sm'),
    },
    geoItemHeader: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    geoItemName: {
        fontSize: 14,
        fontWeight: '500',
        color: getColor('gray.700'),
        flex: 1,
        ...(Platform.OS === 'web' ? { fontFamily: 'Inter, sans-serif' } : {}),
    },
    geoItemCount: {
        fontSize: 14,
        fontWeight: '600',
        color: getColor('gray.900'),
        ...(Platform.OS === 'web' ? { fontFamily: 'Inter, sans-serif' } : {}),
    },
    geoBar: {
        height: 8,
        backgroundColor: getColor('gray.100'),
        borderRadius: getBorderRadius('full'),
        overflow: 'hidden',
    },
    geoBarFill: {
        height: '100%',
        borderRadius: getBorderRadius('full'),
    },
});

export default AnalyticsDashboardScreen;
